import { signal, Signal, WritableSignal } from '@angular/core';
import { GoogleMap } from '@angular/google-maps';
import { CalibrationMode, CalibrationPoint, CalibrationResult, PixelPoint } from 'src/app/interfaces/shared/calibration';
import { LatLon } from 'src/app/interfaces/shared/latLon';
import { CalibrationMath } from '../shared/calibrationMath';
import * as Tool3D from '../shared/threeDTools';

export interface ImageCalibrationOptions {
    map: GoogleMap;
    imageUrl: string;
    imageWidth: number;
    imageHeight: number;
    currentMapCoord: {
        north: number;
        south: number;
        east: number;
        west: number;
    };
    containerElement: HTMLElement;
    onComplete?: (result: CalibrationResult) => void;
    onCancel?: () => void;
}

export class ImageCalibration {
    // Signals
    private readonly modeSignal: WritableSignal<CalibrationMode> = signal('idle');
    public readonly mode: Signal<CalibrationMode> = this.modeSignal.asReadonly();

    private readonly point1Signal: WritableSignal<CalibrationPoint | null> = signal(null);
    public readonly point1: Signal<CalibrationPoint | null> = this.point1Signal.asReadonly();

    private readonly point2Signal: WritableSignal<CalibrationPoint | null> = signal(null);
    public readonly point2: Signal<CalibrationPoint | null> = this.point2Signal.asReadonly();

    private readonly previewResultSignal: WritableSignal<CalibrationResult | null> = signal(null);
    public readonly previewResult: Signal<CalibrationResult | null> = this.previewResultSignal.asReadonly();

    private readonly errorSignal: WritableSignal<string | null> = signal(null);
    public readonly error: Signal<string | null> = this.errorSignal.asReadonly();

    private readonly editingPointSignal: WritableSignal<'point1' | 'point2' | null> = signal(null);
    public readonly editingPoint = this.editingPointSignal.asReadonly();

    private readonly zoomLevelSignal: WritableSignal<number> = signal(1);
    public readonly zoomLevel: Signal<number> = this.zoomLevelSignal.asReadonly();

    private readonly offsetXSignal: WritableSignal<number> = signal(0);
    private readonly offsetYSignal: WritableSignal<number> = signal(0);

    // Variables
    private canvas: HTMLCanvasElement | null = null;
    private ctx: CanvasRenderingContext2D | null = null;
    private image: HTMLImageElement | null = null;
    private mapDiv: HTMLElement | null = null;
    private containerElement: HTMLElement | null = null;

    private map: GoogleMap | null = null;
    private isEnabled = false;
    private imageUrl = '';
    private imageWidth = 0;
    private imageHeight = 0;
    private options: ImageCalibrationOptions | null = null;

    private point1Marker: google.maps.Marker | null = null;
    private point2Marker: google.maps.Marker | null = null;
    private imgOffsetX: number = 0;
    private imgOffsetY: number = 0;
    private zoomTimeout: ReturnType<typeof setTimeout> | null = null;
    private isDragging = false;
    private dragStartX = 0;
    private dragStartY = 0;
    private dragStartOffsetX = 0;
    private dragStartOffsetY = 0;
    private readonly MAX_ZOOM = 4;
    private readonly ZOOM_STEP = 0.2;
    private minZoom: number = 1;

    // callbacks
    private onComplete: ((result: CalibrationResult) => void) | null = null;
    private onCancel: (() => void) | null = null;

    constructor(options: ImageCalibrationOptions) {
        this.options = options;
        this.map = options.map;
        this.imageUrl = options.imageUrl;
        this.imageWidth = options.imageWidth;
        this.imageHeight = options.imageHeight;
        this.containerElement = options.containerElement;
        this.onComplete = options.onComplete || null;
        this.onCancel = options.onCancel || null;
    }

    async enable(): Promise<void> {
        if (this.isEnabled) return;

        try {
            await this.loadImage();
            this.createCanvasOverlay();
            this.setupEventListeners();
            this.draw();

            this.isEnabled = true;
            this.modeSignal.set('editing-point1');
            this.errorSignal.set(null);
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Erreur lors du chargement de l\'image';
            this.errorSignal.set(message);
            console.error('ImageCalibration.enable():', error);
        }
    }

    disable(): void {
        if (!this.isEnabled) return;

        this.cleanup();
        this.isEnabled = false;
        this.modeSignal.set('idle');
    }

    destroy(): void {
        this.disable();
        this.point1Signal.set(null);
        this.point2Signal.set(null);
        this.previewResultSignal.set(null);
        this.canvas = null;
        this.ctx = null;
        this.image = null;
        this.mapDiv = null;
        this.map = null;
        this.options = null;
        this.onComplete = null;
        this.onCancel = null;
    }

    public setEditingPoint(point: 'point1' | 'point2'): void {
        this.editingPointSignal.set(point);
        this.modeSignal.set(`editing-${point}`);
    }

    public exitEditingMode(): void {
        this.editingPointSignal.set(null);
        this.modeSignal.set('preview');
    }

    private createCanvasOverlay(): void {
        if (!this.map?.googleMap) return;

        this.mapDiv = this.containerElement || this.map.googleMap.getDiv() as HTMLElement;
        if (!this.mapDiv) return;

        this.canvas = document.createElement('canvas');
        this.canvas.id = 'image-calibration-canvas';
        this.canvas.width = this.mapDiv.clientWidth;
        this.canvas.height = this.mapDiv.clientHeight;
        this.canvas.style.position = 'absolute';
        this.canvas.style.top = '0';
        this.canvas.style.left = '0';
        this.canvas.style.cursor = 'crosshair';
        this.canvas.style.zIndex = '99';

        this.mapDiv.style.position = 'relative';
        this.mapDiv.appendChild(this.canvas);

        this.ctx = this.canvas.getContext('2d');

        // Calculate initial zoom to fit image
        const ratioX = this.canvas.width / this.imageWidth;
        const ratioY = this.canvas.height / this.imageHeight;
        this.minZoom = Math.min(ratioX, ratioY);

        this.zoomLevelSignal.set(this.minZoom);
        this.offsetXSignal.set(0);
        this.offsetYSignal.set(0);
    }

    private loadImage(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.image = new Image();
            this.image.crossOrigin = 'anonymous';

            this.image.onload = () => resolve();
            this.image.onerror = () => reject(new Error('Impossible de charger l\'image'));

            this.image.src = this.imageUrl;
        });
    }

    private setupEventListeners(): void {
        if (!this.canvas) return;

        this.canvas.addEventListener('click', (e) => this.onCanvasClick(e));
        this.canvas.addEventListener('wheel', (e) => this.onCanvasWheel(e), { passive: false });
        this.canvas.addEventListener('mousedown', (e) => this.onCanvasMouseDown(e));
        this.canvas.addEventListener('mousemove', (e) => this.onCanvasMouseMove(e));
        this.canvas.addEventListener('mouseup', (e) => this.onCanvasMouseUp(e));
        this.canvas.addEventListener('mouseleave', (e) => this.onCanvasMouseLeave(e));
        this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());
        window.addEventListener('resize', () => this.onWindowResize());
    }

    private onCanvasClick(event: MouseEvent): void {
        const mode = this.modeSignal();
        if (mode === 'idle' || mode === 'complete') return;

        const canvas = this.canvas;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;

        const zoom = this.zoomLevelSignal();
        const offsetX = this.offsetXSignal();
        const offsetY = this.offsetYSignal();

        const scaledWidth = this.imageWidth * zoom;
        const scaledHeight = this.imageHeight * zoom;

        const imgX = (canvas.width - scaledWidth) / 2 + offsetX;
        const imgY = (canvas.height - scaledHeight) / 2 + offsetY;

        // Check if click is within the image bounds
        if (x < imgX || x > imgX + scaledWidth || y < imgY || y > imgY + scaledHeight) {
            return;
        }

        const pixelPoint: PixelPoint = {
            x: (x - imgX) / zoom,
            y: (y - imgY) / zoom,
        };

        if (mode === 'editing-point1') {
            this.point1Signal.set({
                id: 'point1',
                pixel: pixelPoint,
                real: { lat: 0, lon: 0 },
                isSet: false,
            });

            if (this.point2Signal()) {
                this.modeSignal.set('edit-coords');
            } else {
                this.modeSignal.set('editing-point2');
            }

            this.draw();
        }
        else if (mode === 'editing-point2') {
            this.point2Signal.set({
                id: 'point2',
                pixel: pixelPoint,
                real: { lat: 0, lon: 0 },
                isSet: false,
            });

            this.modeSignal.set('edit-coords');
            this.draw();
        }
    }

    private onWindowResize(): void {
        if (!this.mapDiv || !this.canvas) return;

        const newWidth = this.mapDiv.clientWidth;
        const newHeight = this.mapDiv.clientHeight;

        if (newWidth === this.canvas.width && newHeight === this.canvas.height) return;

        this.canvas.width = newWidth;
        this.canvas.height = newHeight;

        const ratioX = this.canvas.width / this.imageWidth;
        const ratioY = this.canvas.height / this.imageHeight;
        const newMinZoom = Math.min(ratioX, ratioY);

        if (this.zoomLevelSignal() < newMinZoom) {
            this.zoomLevelSignal.set(newMinZoom);
        }

        this.minZoom = newMinZoom;
        this.draw();
    }

    private canvasToImageCoords(canvasCoords: PixelPoint): PixelPoint {
        return {
            x: canvasCoords.x - this.imgOffsetX,
            y: canvasCoords.y - this.imgOffsetY,
        };
    }

    public calculateFromCoords(lat1: number, lon1: number, lat2: number, lon2: number): void {
        const p1 = this.point1Signal();
        const p2 = this.point2Signal();

        if (!p1 || !p2) return;

        // Mettre à jour les coords
        p1.real = { lat: lat1, lon: lon1 };
        p1.isSet = true;
        this.point1Signal.set({ ...p1 });

        p2.real = { lat: lat2, lon: lon2 };
        p2.isSet = true;
        this.point2Signal.set({ ...p2 });

        this.calculatePreview();
    }

    private calculatePreview(): void {
        const p1 = this.point1Signal();
        const p2 = this.point2Signal();

        if (!p1 || !p2 || !p1.isSet || !p2.isSet) {
            return;
        }

        try {
            const error = CalibrationMath.validateCalibrationPoints(
                { pixel: p1.pixel, real: p1.real },
                { pixel: p2.pixel, real: p2.real }
            );

            if (error) {
                this.errorSignal.set(error);
                return;
            }

            const scale = CalibrationMath.calculateScale(p1.pixel, p2.pixel, p1.real, p2.real);
            const angleSigned = CalibrationMath.calculateAngle(p1.pixel, p2.pixel, p1.real, p2.real);
            const angle360 = ((-angleSigned % 360) + 360) % 360;
            const angleRad = Tool3D.degToRad(angleSigned);

            const cos = Math.cos(angleRad);
            const sin = Math.sin(angleRad);

            const corners: PixelPoint[] = [
                { x: 0, y: 0 },
                { x: this.imageWidth, y: 0 },
                { x: this.imageWidth, y: this.imageHeight },
                { x: 0, y: this.imageHeight },
            ];

            const cornersGPS: LatLon[] = corners.map((corner) => {
                const relX = corner.x - p1.pixel.x;
                const relY = -(corner.y - p1.pixel.y);

                const worldX = (relX * cos - relY * sin) * scale;
                const worldY = (relX * sin + relY * cos) * scale;

                const [lon, lat] = CalibrationMath.xyToGps(
                    worldX,
                    worldY,
                    p1.real.lat,
                    p1.real.lon
                );

                return { lat, lon };
            });

            const lats = cornersGPS.map(c => c.lat);
            const lons = cornersGPS.map(c => c.lon);

            const mapCoord = {
                north: Math.max(...lats),
                south: Math.min(...lats),
                east: Math.max(...lons),
                west: Math.min(...lons),
            };

            const center: LatLon = {
                lat: (mapCoord.north + mapCoord.south) / 2,
                lon: (mapCoord.east + mapCoord.west) / 2
            };

            const result: CalibrationResult = {
                scale,
                angle: angle360,
                mapCoord,
                center,
                imageWidth: this.imageWidth,
                imageHeight: this.imageHeight,
            };

            this.previewResultSignal.set(result);
            this.modeSignal.set('preview');
            this.errorSignal.set(null);
            this.draw();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Erreur lors du calcul';
            this.errorSignal.set(message);
            console.error('ImageCalibration.calculatePreview():', error);
        }
    }

    public confirm(): void {
        const result = this.previewResultSignal();

        if (!result) {
            this.errorSignal.set('Impossible de finaliser le calibrage');
            return;
        }

        if (this.onComplete) {
            this.onComplete(result);
        }
    }

    public cancel(): void {
        this.modeSignal.set('idle');
        this.point1Signal.set(null);
        this.point2Signal.set(null);
        this.previewResultSignal.set(null);
        this.errorSignal.set(null);

        if (this.onCancel) {
            this.onCancel();
        }

        this.disable();
    }

    private draw(): void {
        if (!this.ctx || !this.canvas || !this.image) return;

        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

        const zoom = this.zoomLevelSignal();
        const offsetX = this.offsetXSignal();
        const offsetY = this.offsetYSignal();

        const scaledWidth = this.imageWidth * zoom;
        const scaledHeight = this.imageHeight * zoom;

        const imgX = (this.canvas.width - scaledWidth) / 2 + offsetX;
        const imgY = (this.canvas.height - scaledHeight) / 2 + offsetY;

        this.ctx.drawImage(this.image, imgX, imgY, scaledWidth, scaledHeight);

        this.imgOffsetX = imgX;
        this.imgOffsetY = imgY;

        this.drawPoints(imgX, imgY, scaledWidth, scaledHeight);
    }

    private drawPoints(imgX: number, imgY: number, scaledWidth: number, scaledHeight: number): void {
        if (!this.ctx) return;

        const p1 = this.point1Signal();
        const p2 = this.point2Signal();
        const zoom = this.zoomLevelSignal();

        if (p1) {
            this.drawMarker(
                imgX + p1.pixel.x * zoom,
                imgY + p1.pixel.y * zoom,
                '#FF0000',
                '1',
                zoom
            );
        }

        if (p2) {
            this.drawMarker(
                imgX + p2.pixel.x * zoom,
                imgY + p2.pixel.y * zoom,
                '#0000FF',
                '2',
                zoom
            );
        }

        if (p1 && p2) {
            this.ctx.strokeStyle = 'rgba(0, 255, 0, 0.5)';
            this.ctx.lineWidth = 3;
            this.ctx.beginPath();
            this.ctx.moveTo(imgX + p1.pixel.x * zoom, imgY + p1.pixel.y * zoom);
            this.ctx.lineTo(imgX + p2.pixel.x * zoom, imgY + p2.pixel.y * zoom);
            this.ctx.stroke();
        }
    }

    private drawMarker(x: number, y: number, color: string, label: string, zoom: number): void {
        if (!this.ctx) return;

        const radius = 15;

        // Cercle
        this.ctx.fillStyle = color;
        this.ctx.beginPath();
        this.ctx.arc(x, y, radius, 0, 2 * Math.PI);
        this.ctx.fill();

        // Border
        this.ctx.strokeStyle = 'white';
        this.ctx.lineWidth = 2;
        this.ctx.stroke();

        // Label
        this.ctx.fillStyle = 'white';
        this.ctx.font = 'bold 11px Arial';
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        this.ctx.fillText(label, x, y);
    }

    private onCanvasWheel(event: WheelEvent): void {
        event.preventDefault();

        if (this.zoomTimeout) return;

        this.zoomTimeout = setTimeout(() => {
            this.zoomTimeout = null;
        }, 16);

        const delta = event.deltaY > 0 ? -this.ZOOM_STEP : this.ZOOM_STEP;
        const currentZoom = this.zoomLevelSignal();
        const newZoom = Math.max(
            this.minZoom,
            Math.min(this.MAX_ZOOM, currentZoom + delta)
        );

        if (newZoom === currentZoom) return;

        const canvas = this.canvas;
        if (!canvas) return;

        // Position mouse
        const rect = canvas.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;

        const currentOffsetX = this.offsetXSignal();
        const currentOffsetY = this.offsetYSignal();

        // Zoom image
        const scaledWidth = this.imageWidth * currentZoom;
        const scaledHeight = this.imageHeight * currentZoom;

        const imgX = (canvas.width - scaledWidth) / 2 + currentOffsetX;
        const imgY = (canvas.height - scaledHeight) / 2 + currentOffsetY;

        const mouseRelativeToImgX = mouseX - imgX;
        const mouseRelativeToImgY = mouseY - imgY;

        const newScaledWidth = this.imageWidth * newZoom;
        const newScaledHeight = this.imageHeight * newZoom;

        const imgCoordX = mouseRelativeToImgX / currentZoom;
        const imgCoordY = mouseRelativeToImgY / currentZoom;

        const newImgX = mouseX - imgCoordX * newZoom;
        const newImgY = mouseY - imgCoordY * newZoom;

        const newOffsetX = newImgX - (canvas.width - newScaledWidth) / 2;
        const newOffsetY = newImgY - (canvas.height - newScaledHeight) / 2;

        // Limit offset to prevent showing blank space
        const maxOffsetX = (newScaledWidth - canvas.width) / 2;
        const maxOffsetY = (newScaledHeight - canvas.height) / 2;
        const minOffsetX = -maxOffsetX;
        const minOffsetY = -maxOffsetY;

        const clampedOffsetX = Math.max(minOffsetX, Math.min(maxOffsetX, newOffsetX));
        const clampedOffsetY = Math.max(minOffsetY, Math.min(maxOffsetY, newOffsetY));

        this.zoomLevelSignal.set(newZoom);
        this.offsetXSignal.set(clampedOffsetX);
        this.offsetYSignal.set(clampedOffsetY);

        this.draw();
    }

    private onCanvasMouseDown(event: MouseEvent): void {
        if (event.button !== 2) return;

        const canvas = this.canvas;
        if (!canvas) return;

        this.isDragging = true;
        this.dragStartX = event.clientX;
        this.dragStartY = event.clientY;
        this.dragStartOffsetX = this.offsetXSignal();
        this.dragStartOffsetY = this.offsetYSignal();

        canvas.style.cursor = 'grabbing';
    }

    private onCanvasMouseMove(event: MouseEvent): void {
        if (!this.isDragging) return;

        const canvas = this.canvas;
        if (!canvas) return;

        const deltaX = event.clientX - this.dragStartX;
        const deltaY = event.clientY - this.dragStartY;

        // Calculate new offset with limits
        const zoom = this.zoomLevelSignal();
        const scaledWidth = this.imageWidth * zoom;
        const scaledHeight = this.imageHeight * zoom;

        const maxOffsetX = (scaledWidth - canvas.width) / 2;
        const maxOffsetY = (scaledHeight - canvas.height) / 2;
        const minOffsetX = -maxOffsetX;
        const minOffsetY = -maxOffsetY;

        const newOffsetX = Math.max(minOffsetX, Math.min(maxOffsetX, this.dragStartOffsetX + deltaX));
        const newOffsetY = Math.max(minOffsetY, Math.min(maxOffsetY, this.dragStartOffsetY + deltaY));

        this.offsetXSignal.set(newOffsetX);
        this.offsetYSignal.set(newOffsetY);

        this.draw();
    }

    private onCanvasMouseUp(event: MouseEvent): void {
        if (event.button !== 2) return;

        this.isDragging = false;
        const canvas = this.canvas;

        if (canvas) {
            canvas.style.cursor = 'crosshair';
        }
    }

    private onCanvasMouseLeave(event: MouseEvent): void {
        this.isDragging = false;
        const canvas = this.canvas;
        if (canvas) {
            canvas.style.cursor = 'crosshair';
        }
    }

    private cleanup(): void {
        if (this.canvas && this.canvas.parentElement) {
            this.canvas.parentElement.removeChild(this.canvas);
        }

        if (this.canvas) {
            this.canvas.removeEventListener('click', (e) => this.onCanvasClick(e));
        }

        window.removeEventListener('resize', () => this.onWindowResize());

        if (this.point1Marker) this.point1Marker.setMap(null);
        if (this.point2Marker) this.point2Marker.setMap(null);

        this.canvas = null;
        this.ctx = null;
        this.image = null;
    }

    static degToRad(degrees: number): number {
        return Tool3D.degToRad(degrees);
    }

    static radToDeg(radians: number): number {
        return Tool3D.radToDeg(radians);
    }
}